import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import s from './FidelidadeConfig.module.css';

const CONFIG_PADRAO = {
  ativo: false,
  valor_por_ponto: '',
  valor_minimo_venda: '',
  considerar_somente_avista: false,
  pontos_por_resgate: '',
  valor_credito_resgate: '',
};

export default function FidelidadeConfig() {
  const { usuario } = useAuth();
  const orgId = usuario?.organizacao_id;

  const [carregando, setCarregando] = useState(true);
  const [config, setConfig] = useState(CONFIG_PADRAO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [simulacaoVenda, setSimulacaoVenda] = useState('25,00');

  const setC = (campo, valor) => setConfig(prev => ({ ...prev, [campo]: valor }));

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organizacoes/${orgId}/fidelidade`)
      .then(({ data }) => setConfig({ ...CONFIG_PADRAO, ...data }))
      .catch(() => setErro('Erro ao carregar configurações de fidelidade.'))
      .finally(() => setCarregando(false));
  }, [orgId]);

  const salvar = async () => {
    setErro(''); setSucesso(''); setSalvando(true);
    try {
      const { data } = await api.put(`/organizacoes/${orgId}/fidelidade`, config);
      setConfig(data);
      setSucesso('Configurações salvas com sucesso!');
      setTimeout(() => setSucesso(''), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar configurações.');
    } finally {
      setSalvando(false);
    }
  };

  // Simulação ao vivo — usa os valores DIGITADOS na tela (mesmo antes de
  // salvar), pra usuária conferir na hora se entendeu certo o efeito
  // combinado dos campos, sem precisar testar numa venda de verdade.
  const valorPorPonto = parseFloat(config.valor_por_ponto) || 0;
  const valorMinimo = parseFloat(config.valor_minimo_venda) || 0;
  const pontosPorLote = parseInt(config.pontos_por_resgate) || 0;
  const creditoPorLote = parseFloat(config.valor_credito_resgate) || 0;
  const valorSimulado = parseFloat(String(simulacaoVenda).replace(',', '.')) || 0;
  const pontosGanhos = (valorPorPonto > 0 && valorSimulado >= valorMinimo)
    ? Math.floor(valorSimulado / valorPorPonto) : 0;
  const lotesGanhos = pontosPorLote > 0 ? Math.floor(pontosGanhos / pontosPorLote) : 0;
  const creditoGerado = (lotesGanhos * creditoPorLote).toFixed(2).replace('.', ',');

  if (carregando) return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>⭐</span> Fidelidade</div>
      <div className={s.conteudo}>Carregando...</div>
    </div>
  );

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>⭐</span> Fidelidade</div>

      <div className={s.conteudo}>
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Programa de Fidelidade</legend>

          <label className={s.checkRow}>
            <input type="checkbox" checked={!!config.ativo} onChange={e => setC('ativo', e.target.checked)} />
            Programa ativo (clientes acumulam e podem resgatar pontos)
          </label>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Acumular Pontos</legend>

          <div className={s.campo}>
            <span>Cliente ganha 1 ponto a cada R$</span>
            <input className={s.inputCurto} type="number" step="0.01" min="0"
              value={config.valor_por_ponto}
              onChange={e => setC('valor_por_ponto', e.target.value)} />
            <span>em compras.</span>
          </div>
          <p className={s.ajudaCampo}>
            Ex: com R$ 15,00 aqui, uma venda de R$ 25,00 gera 1 ponto (R$ 30,00 geraria 2). Esse é
            o valor que "custa" ganhar 1 ponto — não é quanto o ponto vale no resgate (isso é
            definido embaixo, em "Resgatar Pontos").
          </p>

          <div className={s.campo}>
            <span className={s.campoLabel}>Valor mínimo da venda para acumular pontos R$:</span>
            <input className={s.inputErp} type="number" step="0.01" min="0"
              value={config.valor_minimo_venda}
              onChange={e => setC('valor_minimo_venda', e.target.value)} />
          </div>
          <p className={s.ajudaCampo}>
            Venda abaixo desse valor não gera ponto NENHUM (é só um piso — não muda a taxa acima).
          </p>

          <label className={s.checkRow}>
            <input type="checkbox" checked={!!config.considerar_somente_avista}
              onChange={e => setC('considerar_somente_avista', e.target.checked)} />
            Considerar somente o valor à vista
          </label>
          <p className={s.ajudaCampo}>
            Com essa opção marcada, valores pagos em Boleto, Crediário ou Cartão de Crédito
            parcelado não contam pra base de cálculo dos pontos — só Dinheiro, PIX, Débito e
            Crédito à vista (1x).
          </p>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Resgatar Pontos</legend>
          <div className={s.campo}>
            <span>A cada</span>
            <input className={s.inputCurto} type="number" step="1" min="1"
              value={config.pontos_por_resgate}
              onChange={e => setC('pontos_por_resgate', e.target.value)} />
            <span>pontos resgatados o cliente recebe R$</span>
            <input className={s.inputCurto} type="number" step="0.01" min="0"
              value={config.valor_credito_resgate}
              onChange={e => setC('valor_credito_resgate', e.target.value)} />
            <span>de crédito.</span>
          </div>
          <p className={s.ajudaCampo}>
            O crédito só fica disponível em lotes completos — ex: com 10 pontos = R$ 2,00,
            um cliente com 25 pontos tem R$ 4,00 disponíveis pra usar como forma de pagamento
            no PDV (5 pontos ficam guardados até completar o próximo lote).
          </p>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Simulação</legend>
          <div className={s.campo}>
            <span className={s.campoLabel}>Numa venda de R$:</span>
            <input className={s.inputErp} type="text" inputMode="decimal"
              value={simulacaoVenda}
              onChange={e => setSimulacaoVenda(e.target.value)} />
          </div>
          <p className={s.ajudaCampo} style={{ fontSize: '0.85rem', color: 'var(--text)' }}>
            {valorPorPonto <= 0 || pontosPorLote <= 0
              ? 'Preencha "Valor equivalente a um ponto" e a regra de resgate pra ver a simulação.'
              : valorSimulado < valorMinimo
                ? `Essa venda fica ABAIXO do valor mínimo (R$ ${valorMinimo.toFixed(2).replace('.', ',')}) — não gera nenhum ponto.`
                : (
                  <>
                    o cliente ganha <strong>{pontosGanhos} ponto(s)</strong>
                    {pontosGanhos > 0 && lotesGanhos === 0 && (
                      <> — ainda não dá pra resgatar (faltam {pontosPorLote - pontosGanhos} ponto(s) pro 1º lote de {pontosPorLote})</>
                    )}
                    {lotesGanhos > 0 && (
                      <> = <strong>R$ {creditoGerado}</strong> disponível(is) pra usar como pagamento no PDV</>
                    )}
                    .
                  </>
                )}
          </p>
        </fieldset>

        {erro    && <div className={s.erroMsg}>{erro}</div>}
        {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}
      </div>

      <div className={s.rodape}>
        <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando} style={{ marginLeft: 'auto' }}>
          {salvando ? 'Salvando...' : '✔ Salvar'}
        </button>
      </div>
    </div>
  );
}
